// Gemensam begreppsdatabas och funktionen som markerar begrepp i lärotexten.
import type { Element, Root, RootContent, Text } from 'hast'
import { SKIP, visit } from 'unist-util-visit'
import type { BegreppPost } from '../types'
import begreppJson from '../content/begrepp.json'

export const allaBegrepp = begreppJson as BegreppPost[]

const postPerSlug = new Map(allaBegrepp.map((b) => [b.slug, b]))
const slugPerNamn = new Map(allaBegrepp.map((b) => [b.begrepp.toLowerCase(), b.slug]))

/** Begreppet bakom en /begrepp/<slug>-adress. */
export const hittaBegrepp = (slug: string): BegreppPost | null => postPerSlug.get(slug) ?? null

/** Slug för ett begreppsnamn (t.ex. för att länka kapitlens begreppslistor). */
export const slugForBegrepp = (namn: string): string | null => slugPerNamn.get(namn.toLowerCase()) ?? null

/** Begreppen A–Ö, grupperade på inledande bokstav (svensk sortering). */
export function begreppAO(): { bokstav: string; poster: BegreppPost[] }[] {
  const sorterade = [...allaBegrepp].sort((a, b) => a.begrepp.localeCompare(b.begrepp, 'sv'))
  const grupper: { bokstav: string; poster: BegreppPost[] }[] = []
  for (const post of sorterade) {
    const bokstav = post.begrepp[0].toUpperCase()
    const sista = grupper[grupper.length - 1]
    if (sista && sista.bokstav === bokstav) sista.poster.push(post)
    else grupper.push({ bokstav, poster: [post] })
  }
  return grupper
}

const escapaRegex = (s: string) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')

// Bara etablerade svenska böjningsändelser accepteras som suffix efter ett begrepp.
// Ett tidigare försök tillät "vilka bokstäver som helst" (upp till fyra), vilket
// fick korta begrepp att råka matcha helt andra ord som bara börjar likadant —
// t.ex. matchade begreppet "norm" felaktigt in i "normal" (norm + "al"). Genom
// att bara acceptera riktiga böjningsändelser filtreras såna falska träffar bort,
// samtidigt som riktiga böjningar (bestämd form, plural, genitiv) hittas.
const BOJNINGSANDELSER = [
  'ernas', 'arnas', 'ornas',
  'erna', 'arna', 'orna',
  'ens', 'ets', 'are', 'ast',
  'en', 'et', 'ar', 'or', 'er', 'na',
  'n', 't', 's', 'a',
]

// Längsta begrepp först så att t.ex. "scientific management" vinner över kortare träffar.
const ordnade = [...allaBegrepp].sort((a, b) => b.begrepp.length - a.begrepp.length)
const postPerNyckel = new Map(allaBegrepp.map((b) => [b.begrepp.toLowerCase(), b]))

// Matchar ett begrepp som eget ord, med en valfri böjningsändelse (se ovan) så
// att svenska böjningsformer ("teorier", "chefens", "hierarkiska") också hittas.
const monster = new RegExp(
  `(?<![\\p{L}\\p{N}])(${ordnade.map((b) => escapaRegex(b.begrepp)).join('|')})(${BOJNINGSANDELSER.join('|')})?(?![\\p{L}\\p{N}])`,
  'giu',
)

export interface TextSegment {
  text: string
  begrepp?: BegreppPost
}

/**
 * Delar upp ett textstycke i segment där förekomster av begrepp är uppmärkta.
 * `redanMarkerade` delas mellan anrop så att varje begrepp bara markeras en
 * gång per avsnitt — annars blir texten plottrig.
 */
export function markeraText(text: string, redanMarkerade: Set<string>): TextSegment[] {
  const segment: TextSegment[] = []
  let pos = 0
  for (const traff of text.matchAll(monster)) {
    const nyckel = traff[1].toLowerCase()
    const post = postPerNyckel.get(nyckel)
    if (!post || redanMarkerade.has(nyckel)) continue
    redanMarkerade.add(nyckel)
    const heleTraffen = traff[1] + (traff[2] ?? '')
    if (traff.index > pos) segment.push({ text: text.slice(pos, traff.index) })
    segment.push({ text: heleTraffen, begrepp: post })
    pos = traff.index + heleTraffen.length
  }
  if (pos < text.length) segment.push({ text: text.slice(pos) })
  return segment
}

const INTE_MARKERBARA_TAGGAR = new Set(['code', 'pre'])

/**
 * Rehype-plugin (för react-markdown) som markerar begrepp i den renderade
 * markdown-texten. En egen instans skapas per markdown-rendering (react-markdown
 * bygger om sin processor vid varje render), så `redanMarkerade` nollställs
 * naturligt per avsnitt/ingress i stället för att delas globalt över hela sidan.
 */
export function begreppRehypePlugin() {
  const redanMarkerade = new Set<string>()
  return (tree: Root) => {
    visit(tree, 'text', (node: Text, index, parent) => {
      if (typeof index !== 'number' || !parent) return
      if (parent.type === 'element' && INTE_MARKERBARA_TAGGAR.has((parent as Element).tagName)) return

      const segment = markeraText(node.value, redanMarkerade)
      if (segment.length === 1 && !segment[0].begrepp) return

      const nyaNoder: RootContent[] = segment.map((del) =>
        del.begrepp
          ? ({
              type: 'element',
              tagName: 'span',
              properties: {
                // hast vill ha DOM-egenskapernas camelCase-namn (className,
                // tabIndex, ariaLabel, dataForklaring) — inte HTML-attributens
                // bindestreck-namn — annars renderas de inte alls.
                className: ['begrepp-mark'],
                tabIndex: 0,
                ariaLabel: `${del.text}. Begreppsförklaring: ${del.begrepp.forklaring}`,
                dataForklaring: del.begrepp.forklaring,
              },
              children: [{ type: 'text', value: del.text }],
            } satisfies Element)
          : ({ type: 'text', value: del.text } satisfies Text),
      )

      parent.children.splice(index, 1, ...nyaNoder)
      return [SKIP, index + nyaNoder.length]
    })
  }
}
